import { supabase } from "~/integrations/supabase/client";

const BUCKET_NAME = "uploads";
const FOLDER_NAME = "product-images";

export interface ProductImageUploadResult {
  success: boolean;
  imageUrl?: string;
  error?: string;
}

/**
 * Uploads a product image to Supabase storage
 */
export const uploadProductImage = async (
  file: File
): Promise<ProductImageUploadResult> => {
  try {
    // Validate file type
    if (!file.type.startsWith("image/")) {
      return {
        success: false,
        error: "El archivo debe ser una imagen válida (PNG, JPG, GIF)",
      };
    }

    // Validate file size (5MB limit)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      return {
        success: false,
        error: "El archivo es demasiado grande. Máximo 5MB permitido",
      };
    }

    // Generate unique filename
    const timestamp = new Date().getTime();
    const fileExt = file.name.split(".").pop();
    const sanitizedFileName = file.name
      .replace(/[^a-zA-Z0-9.-]/g, "_")
      .toLowerCase();
    const fileName = `${timestamp}_${sanitizedFileName}`;
    const filePath = `${FOLDER_NAME}/${fileName}`;

    console.log(`Uploading product image to ${BUCKET_NAME}/${filePath}`);

    // Upload file to storage
    const { data, error } = await supabase.storage
      .from(BUCKET_NAME)
      .upload(filePath, file, {
        cacheControl: "3600",
        upsert: false,
      });

    if (error) {
      console.error("Product image upload error:", error.message);
      return {
        success: false,
        error: `Error al subir la imagen: ${error.message}`,
      };
    }

    if (!data || !data.path) {
      console.error("Upload succeeded but no path was returned");
      return {
        success: false,
        error: "Error al obtener la ruta de la imagen subida",
      };
    }

    // Get public URL
    const { data: urlData } = supabase.storage
      .from(BUCKET_NAME)
      .getPublicUrl(data.path);

    if (!urlData || !urlData.publicUrl) {
      console.error("Failed to get public URL for uploaded image");
      return {
        success: false,
        error: "Error al generar la URL pública de la imagen",
      };
    }

    console.log(`Product image uploaded successfully: ${urlData.publicUrl}`);

    return {
      success: true,
      imageUrl: urlData.publicUrl,
    };
  } catch (error) {
    console.error("Unexpected error during product image upload:", error);
    return {
      success: false,
      error: "Error inesperado al subir la imagen",
    };
  }
};

/**
 * Deletes a product image from Supabase storage
 */
export const deleteProductImage = async (
  imageUrl: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    // Extract the file path from the URL
    const url = new URL(imageUrl);
    const pathSegments = url.pathname.split("/");
    const bucketIndex = pathSegments.indexOf(BUCKET_NAME);

    if (bucketIndex === -1) {
      return {
        success: false,
        error: "URL de imagen inválida",
      };
    }

    const filePath = pathSegments.slice(bucketIndex + 1).join("/");

    if (!filePath.startsWith(FOLDER_NAME)) {
      return {
        success: false,
        error: "El archivo no pertenece a la carpeta de imágenes de productos",
      };
    }

    console.log(`Deleting product image: ${filePath}`);

    const { error } = await supabase.storage
      .from(BUCKET_NAME)
      .remove([filePath]);

    if (error) {
      console.error("Product image deletion error:", error.message);
      return {
        success: false,
        error: `Error al eliminar la imagen: ${error.message}`,
      };
    }

    console.log(`Product image deleted successfully: ${filePath}`);
    return { success: true };
  } catch (error) {
    console.error("Unexpected error during product image deletion:", error);
    return {
      success: false,
      error: "Error inesperado al eliminar la imagen",
    };
  }
};

/**
 * Validates if a URL is a valid product image URL
 */
export const isValidProductImageUrl = (url: string): boolean => {
  try {
    const urlObj = new URL(url);
    const pathSegments = urlObj.pathname.split("/");
    const bucketIndex = pathSegments.indexOf(BUCKET_NAME);

    if (bucketIndex === -1) return false;

    const filePath = pathSegments.slice(bucketIndex + 1).join("/");
    return filePath.startsWith(FOLDER_NAME);
  } catch {
    return false;
  }
};
